import { ArrowLeft, ArrowRight, Camera, Languages, MoonStar, ScanLine, Smartphone, SunMedium } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router";
import { useAppPreferences } from "../../lib/preferences";
import { AppInstallLinks } from "./AppInstallLinks";
import { WaitlistForm } from "./WaitlistForm";
import "./onboarding.css";

export function JoinPage() {
  const { direction, language, setLanguage, setThemePreference, t, themePreference } = useAppPreferences();
  const BackArrow = direction === "rtl" ? ArrowRight : ArrowLeft;
  const ThemeIcon = themePreference === "dark" ? SunMedium : MoonStar;
  // No production rollout assumption, query string, fragment, or contact data in the QR.
  const joinUrl = typeof window === "undefined" ? null : new URL("/join", window.location.origin).href;

  return (
    <div className="join-page" dir={direction}>
      <a className="join-skip sr-only focus:not-sr-only" href="#join-main">{t("onboarding.skip")}</a>
      <header className="join-header mx-auto flex max-w-[1120px] flex-wrap items-center justify-between gap-4 px-5 py-5 sm:px-8">
        <Link aria-label={t("onboarding.home")} className="join-brand" dir="ltr" to="/">GAAMED</Link>
        <div className="flex flex-wrap gap-2">
          <button
            aria-label={t(language === "ar" ? "preferences.language.toggleToEn" : "preferences.language.toggleToAr")}
            className="join-preference" onClick={() => setLanguage(language === "ar" ? "en" : "ar")} type="button"
          >
            <Languages aria-hidden="true" size={18} />
            <span lang={language === "ar" ? "en" : "ar"}>{language === "ar" ? "English" : "العربية"}</span>
          </button>
          <button
            aria-label={t(themePreference === "dark" ? "preferences.theme.toggleToLight" : "preferences.theme.toggleToDark")}
            className="join-preference" onClick={() => setThemePreference(themePreference === "dark" ? "light" : "dark")} type="button"
          >
            <ThemeIcon aria-hidden="true" size={18} />
          </button>
        </div>
      </header>
      <main className="mx-auto grid max-w-[1120px] gap-8 px-5 pb-12 pt-5 sm:px-8 sm:pt-10 lg:grid-cols-[1.05fr_1fr] lg:gap-x-16 lg:gap-y-10 lg:pt-14" id="join-main" tabIndex={-1}>
        <section aria-labelledby="join-title" className="min-w-0">
          <Link className="join-text-link mb-8 gap-2 text-sm" to="/">
            <BackArrow aria-hidden="true" size={16} /> {t("onboarding.back")}
          </Link>
          <p className="join-eyebrow mb-4">{t("onboarding.eyebrow")}</p>
          <h1 className="join-title mb-5" id="join-title">{t("onboarding.title")}</h1>
          <p className="max-w-md text-base text-[var(--color-text-soft)] sm:text-lg">{t("onboarding.body")}</p>
          <AppInstallLinks />
          <div className="join-availability mt-7 flex items-start gap-3">
            <Smartphone aria-hidden="true" className="mt-1 shrink-0 text-[var(--color-accent)]" size={21} strokeWidth={1.8} />
            <div className="min-w-0">
              <h2 className="mb-2 text-sm font-bold">{t("onboarding.availability.title")}</h2>
              <p className="text-sm text-[var(--color-text-soft)]">{t("onboarding.availability.body")}</p>
            </div>
          </div>
        </section>
        <section className="join-form-panel min-w-0 p-6 sm:p-8 lg:col-start-2 lg:row-span-2 lg:row-start-1" aria-label={t("onboarding.form.title")}>
          <WaitlistForm />
        </section>
        <section className="join-share min-w-0" aria-labelledby="join-share-title">
          {joinUrl && (
            <div className="join-qr">
              <QRCodeSVG
                aria-label={t("onboarding.qrLabel")} bgColor="#ffffff" fgColor="#111111" level="M"
                marginSize={4} role="img" size={176} title={t("onboarding.qrLabel")} value={joinUrl}
              />
            </div>
          )}
          <div className="min-w-0">
            <h2 className="mb-2 flex items-center gap-2 text-base font-bold" id="join-share-title">
              <ScanLine aria-hidden="true" className="shrink-0" size={18} /> {t("onboarding.share.title")}
            </h2>
            <p className="mb-2 text-sm text-[var(--color-text-soft)]">{t("onboarding.share.body")}</p>
            <a className="join-text-link join-url text-sm" dir="ltr" href={joinUrl ?? "/join"}>{joinUrl ?? "/join"}</a>
          </div>
        </section>
      </main>
      <footer className="mx-auto flex max-w-[1120px] flex-wrap items-center justify-between gap-3 px-5 py-6 text-xs text-[var(--color-text-soft)] sm:px-8">
        <span>© {new Date().getFullYear()} Gaamed</span>
        <div className="flex flex-wrap items-center gap-5">
          <a className="join-text-link gap-2" dir="ltr" href="https://www.instagram.com/gaamed.eg" rel="noopener noreferrer" target="_blank">
            <Camera aria-hidden="true" size={16} /> Instagram
          </a>
          <Link className="join-text-link" to="/legal/privacy">{t("landing.footer.privacy")}</Link>
        </div>
      </footer>
    </div>
  );
}
